import { useMemo, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { toast } from 'react-toastify';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick, setDsViewPreference, showConfirmModal } from 'redux/mode/actions';
import { MenuItemAction } from 'menu/MenuItem';
import DropdownMenu from 'components/Widget/DropdownMenu';
import useScrollMemory from 'hooks/useScrollMemory';
import Icon, { IconType } from 'components/Icon';
import ViewToggle from 'components/Widget/ViewToggle';
import forge from 'apis/forge';
import { uploadFile } from 'utils';
import { Attachment } from 'dragonmouse-shared/interfaces';
import JSZip from 'jszip';
import * as Base from '../styles';
import * as S from './styles';
import { AssetCategory } from 'designSystem/content/types';
import { VITE_FORGE_URL } from '@constants';
import { useTheme } from 'styled-components';
import * as Sentry from '@sentry/react';

// Wraps callback-based uploadFile as a promise for sequential await
const uploadFileToStorage = (file: File): Promise<Attachment> =>
	new Promise((resolve, reject) => {
		uploadFile(file, {
			onLoadingStart: () => {},
			onLoadingEnd: () => {},
			onSuccess: resolve,
			onError: reject,
		});
	});

// Conservative heuristic: only match when confident, default to other
const categorizeAsset = (filename: string): AssetCategory => {
	const lower = filename.toLowerCase();
	if (
		lower.includes('logo') ||
		lower.includes('wordmark') ||
		lower.includes('brandmark') ||
		lower.includes('favicon')
	)
		return 'logos';
	if (lower.includes('icon')) return 'icons';
	if (
		lower.includes('illustration') ||
		lower.includes('hero') ||
		lower.includes('avatar') ||
		lower.includes('empty-state') ||
		lower.includes('onboarding') ||
		lower.includes('banner')
	)
		return 'illustrations';
	if (
		lower.includes('pattern') ||
		lower.includes('texture') ||
		lower.includes('bg') ||
		lower.includes('background') ||
		lower.includes('tile')
	)
		return 'patterns';
	return 'other';
};

const AssetsView = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { designSystemContent, activeTab, activeDesignSystemId, viewMode } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			activeDesignSystemId: r.mode.activeDesignSystemId,
			viewMode: r.mode.dsViewPreferences.assetsViewMode ?? 'grid',
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isDownloadingAll, setIsDownloadingAll] = useState(false);
	const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set());
	const [uploadTargetCategory, setUploadTargetCategory] = useState<AssetCategory | null>(null);
	const [listSortKey, setListSortKey] = useState<'name' | 'category'>('name');
	const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('asc');
	const fileInputRef = useRef<HTMLInputElement>(null);

	const assets = designSystemContent?.assets?.all || [];

	const sortedAssets = useMemo(() => {
		const sorted = [...assets].sort((a, b) => {
			let cmp: number;
			if (listSortKey === 'category') {
				cmp = a.category.localeCompare(b.category, undefined, { sensitivity: 'base' });
				// Secondary sort by name when categories match
				if (cmp === 0) cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
			} else {
				cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
			}
			return listSortDir === 'desc' ? -cmp : cmp;
		});
		return sorted;
	}, [assets, listSortKey, listSortDir]);

	// Group assets by category
	const filteredByCategory = useMemo(() => {
		const grouped: Record<AssetCategory, typeof assets> = {
			logos: [],
			icons: [],
			illustrations: [],
			patterns: [],
			other: [],
		};
		for (const asset of assets) {
			grouped[asset.category].push(asset);
		}
		return grouped;
	}, [assets]);

	const categoryLabels: Record<AssetCategory, string> = {
		logos: 'LOGOS',
		icons: 'ICONS',
		illustrations: 'ILLUSTRATIONS',
		patterns: 'PATTERNS',
		other: 'OTHER',
	};

	const categoryColors: Record<AssetCategory, string> = {
		logos: theme.orange[300],
		icons: theme.green[400],
		illustrations: theme.red[500],
		patterns: theme.blue[500],
		other: theme.gray[600],
	};

	const getAssetUrl = (casHash: string): string => {
		return `${VITE_FORGE_URL}/attachment/${encodeURIComponent(casHash)}`;
	};

	const onDeleteAsset = (asset: { path: string; name: string }) => {
		if (!activeDesignSystemId) return;
		dispatch(
			showConfirmModal({
				title: `Delete ${asset.name}?`,
				description: 'This will remove the asset from the current draft.',
				submitLabel: 'Delete',
				onSubmit: async () => {
					try {
						await forge.updateDsFiles({
							designSystemId: activeDesignSystemId,
							files: [{ path: asset.path, content: '' }],
						});
						toast.success('Asset deleted');
					} catch (e) {
						toast.error('Failed to delete asset');
					}
				},
			})
		);
	};

	const onDownloadAsset = async (asset: { name: string; casHash: string }) => {
		try {
			// Proxy mode serves bytes directly with permissive CORS; the default
			// route redirects to a GCS signed URL which fetch can't read cross-origin.
			const url = `${getAssetUrl(asset.casHash)}?proxy=true`;
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Download failed: ${response.status} ${response.statusText}`);
			}
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = asset.name;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(blobUrl);
		} catch (e) {
			toast.error('Failed to download asset');
		}
	};

	const onDownloadAll = async () => {
		if (assets.length === 0 || isDownloadingAll) return;
		setIsDownloadingAll(true);
		const run = async () => {
			const zip = new JSZip();
			const usedNames = new Map<string, number>();
			const fetched = await Promise.all(
				assets.map(async (asset) => {
					try {
						const url = `${getAssetUrl(asset.casHash)}?proxy=true`;
						const response = await fetch(url);
						if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
						const blob = await response.blob();
						return { asset, blob };
					} catch (e) {
						Sentry.captureException(e);
						return { asset, blob: null as Blob | null };
					}
				})
			);
			let skipped = 0;
			for (const { asset, blob } of fetched) {
				if (!blob) {
					skipped += 1;
					continue;
				}
				const filename = asset.path.split('/').pop() || asset.name;
				const key = `${asset.category}/${filename}`;
				const count = usedNames.get(key) ?? 0;
				usedNames.set(key, count + 1);
				const dot = filename.lastIndexOf('.');
				const base = dot > 0 ? filename.slice(0, dot) : filename;
				const ext = dot > 0 ? filename.slice(dot) : '';
				const name = count === 0 ? filename : `${base}-${count}${ext}`;
				zip.folder(asset.category)?.file(name, blob);
			}
			if (usedNames.size === 0) throw new Error('No assets could be downloaded');
			const zipBlob = await zip.generateAsync({ type: 'blob' });
			const blobUrl = URL.createObjectURL(zipBlob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = 'assets.zip';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(blobUrl);
			return skipped;
		};
		try {
			const skipped = await toast.promise(run(), {
				pending: 'Preparing assets…',
				success: 'Assets downloaded',
				error: 'Failed to download assets',
			});
			if (skipped > 0) toast.warning(`${skipped} asset${skipped === 1 ? '' : 's'} skipped`);
		} finally {
			setIsDownloadingAll(false);
		}
	};

	const onMoveToCategory = async (
		asset: { path: string; name: string; casHash: string; category: AssetCategory },
		newCategory: AssetCategory
	) => {
		if (!activeDesignSystemId || asset.category === newCategory) return;
		const filename = asset.path.split('/').pop() ?? asset.name;
		const newPath = `assets/${newCategory}/${filename}`;
		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystemId,
				files: [
					{ path: asset.path, content: '' },
					{ path: newPath, content: asset.casHash },
				],
			});
			setRecentlyAdded((prev) => new Set([...prev, newPath]));
		} catch (e) {
			toast.error('Failed to move asset');
		}
	};

	const getMoveMenuItems = (asset: {
		path: string;
		name: string;
		casHash: string;
		category: AssetCategory;
	}) => {
		const otherCategories = (
			['logos', 'icons', 'illustrations', 'patterns', 'other'] as AssetCategory[]
		).filter((c) => c !== asset.category);
		return otherCategories.map(
			(cat) => new MenuItemAction(categoryLabels[cat], '', () => onMoveToCategory(asset, cat))
		);
	};

	const onUploadFiles = async (files: File[], targetCategory?: AssetCategory) => {
		if (!activeDesignSystemId || !files.length) return;
		setIsUploading(true);
		try {
			const attachments: Attachment[] = [];
			for (const file of files) {
				try {
					const attachment = await uploadFileToStorage(file);
					attachments.push(attachment);
				} catch (e) {
					toast.error(`Failed to upload ${file.name}`);
				}
			}
			if (attachments.length === 0) return;

			const newPaths: string[] = [];
			const updates = attachments.map((attachment) => {
				const category = targetCategory ?? categorizeAsset(attachment.filename);
				const safeName = attachment.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
				const path = `assets/${category}/${safeName}`;
				newPaths.push(path);
				return { path, content: attachment.storagePath };
			});

			await forge.updateDsFiles({
				designSystemId: activeDesignSystemId,
				files: updates,
			});
			setRecentlyAdded((prev) => new Set([...prev, ...newPaths]));
		} catch (e) {
			toast.error('Failed to add assets');
		} finally {
			setIsUploading(false);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setIsDraggingFile(false);
		}
	};

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);
		e.preventDefault();
		if (isUploading) {
			toast.info('Upload in progress. Please wait.');
			return;
		}
		const files = Array.from(e.dataTransfer.files);
		if (files.length) onUploadFiles(files);
	};

	const onToggleSort = (key: 'name' | 'category') => {
		if (listSortKey === key) {
			setListSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setListSortKey(key);
			setListSortDir('asc');
		}
	};

	const onUploadClick = (targetCategory?: AssetCategory) => {
		setUploadTargetCategory(targetCategory ?? null);
		fileInputRef.current?.click();
	};

	const onFileInputChange = () => {
		if (isUploading) {
			toast.info('Upload in progress. Please wait.');
			return;
		}
		const input = fileInputRef.current;
		const files = input?.files;
		if (files?.length && input) {
			onUploadFiles(Array.from(files), uploadTargetCategory ?? undefined);
			setUploadTargetCategory(null);
			input.value = '';
		}
	};

	if (assets.length === 0) {
		return (
			<Base.SectionBase
				ref={scrollRef}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				style={{ position: 'relative' }}
			>
				<Base.SectionHeader>
					<Base.SectionTitle>Assets</Base.SectionTitle>
					<S.HeaderControls>
						<S.ToolbarButton
							onClick={() => onUploadClick()}
							disabled={!activeDesignSystemId || isUploading}
						>
							<Icon type={IconType.UPLOAD} width={16} height={16} />
							Upload
						</S.ToolbarButton>
					</S.HeaderControls>
				</Base.SectionHeader>
				<Base.SectionContent>
					Drag files here or click Upload to add assets to your design system.
				</Base.SectionContent>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					style={{ display: 'none' }}
					onChange={onFileInputChange}
					data-testid="ds-assets-file-input"
				/>
				{isDraggingFile && <S.DropOverlay>Drop files to add assets</S.DropOverlay>}
			</Base.SectionBase>
		);
	}

	return (
		<Base.SectionBase
			ref={scrollRef}
			onDragOver={onDragOver}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
			style={{ position: 'relative' }}
		>
			<Base.SectionHeader>
				<Base.SectionTitle>
					Assets <S.TitleCount>({assets.length})</S.TitleCount>
				</Base.SectionTitle>
				<S.HeaderControls>
					<ViewToggle
						options={[
							{ value: 'grid', icon: IconType.GRID, title: 'Grid view' },
							{ value: 'list', icon: IconType.STACK, title: 'List view' },
						]}
						value={viewMode}
						onChange={(v) =>
							dispatch(setDsViewPreference({ assetsViewMode: v as 'grid' | 'list' }))
						}
					/>
					<S.ToolbarButton
						onClick={onDownloadAll}
						disabled={isDownloadingAll}
						title="Download all assets as a zip"
					>
						<Icon type={IconType.LINE_ARROW_DOWN} width={16} height={16} />
						Download all
					</S.ToolbarButton>
					<S.ToolbarButton
						onClick={() => onUploadClick()}
						disabled={!activeDesignSystemId || isUploading}
					>
						<Icon type={IconType.UPLOAD} width={16} height={16} />
						Upload
					</S.ToolbarButton>
				</S.HeaderControls>
			</Base.SectionHeader>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				style={{ display: 'none' }}
				onChange={onFileInputChange}
				data-testid="ds-assets-file-input"
			/>
			{isDraggingFile && <S.DropOverlay>Drop files to add assets</S.DropOverlay>}
			<Base.SectionContent>
				{viewMode === 'list' ? (
					<S.AssetTable>
						<S.AssetTableHeader>
							<S.AssetTableHeaderCell $sortable onClick={() => onToggleSort('name')}>
								Name
								<S.SortIndicator $active={listSortKey === 'name'}>
									<Icon
										type={
											listSortKey === 'name' && listSortDir === 'desc'
												? IconType.LINE_ARROW_UP
												: IconType.LINE_ARROW_DOWN
										}
										width={12}
										height={12}
										color={listSortKey === 'name' ? theme.gray[1000] : theme.gray[400]}
									/>
								</S.SortIndicator>
							</S.AssetTableHeaderCell>
							<S.AssetTableHeaderCell>Path</S.AssetTableHeaderCell>
							<S.AssetTableHeaderCell $sortable onClick={() => onToggleSort('category')}>
								Category
								<S.SortIndicator $active={listSortKey === 'category'}>
									<Icon
										type={
											listSortKey === 'category' && listSortDir === 'desc'
												? IconType.LINE_ARROW_UP
												: IconType.LINE_ARROW_DOWN
										}
										width={12}
										height={12}
										color={listSortKey === 'category' ? theme.gray[1000] : theme.gray[400]}
									/>
								</S.SortIndicator>
							</S.AssetTableHeaderCell>
							<div />
						</S.AssetTableHeader>
						{sortedAssets.map((asset, index) => {
							return (
								<S.AssetTableRow
									key={asset.path}
									$isNew={recentlyAdded.has(asset.path)}
									$odd={index % 2 === 1}
								>
									<S.AssetTableNameCell>
										<S.AssetTableThumbnail>
											{asset.mimeType.startsWith('image/') ? (
												<S.AssetTableThumbnailImg
													src={getAssetUrl(asset.casHash)}
													alt={asset.name}
												/>
											) : (
												<span style={{ fontSize: 10, color: 'inherit' }}>{asset.mimeType}</span>
											)}
										</S.AssetTableThumbnail>
										<S.AssetTableName>{asset.name}</S.AssetTableName>
									</S.AssetTableNameCell>
									<S.AssetTablePath>{asset.path}</S.AssetTablePath>
									<S.AssetTableCategorySelect
										value={asset.category}
										onChange={(e) => {
											const newCat = e.target.value as AssetCategory;
											onMoveToCategory(asset, newCat);
										}}
									>
										{(Object.keys(categoryLabels) as AssetCategory[]).map((cat) => (
											<option key={cat} value={cat}>
												{categoryLabels[cat]}
											</option>
										))}
									</S.AssetTableCategorySelect>
									<S.AssetTableActions>
										<S.AssetTableActionButton
											onClick={() =>
												dispatch(toggleDsPick({ kind: 'asset', assetPath: asset.path }))
											}
											title="Add to chat"
											aria-label="Add to chat"
										>
											<Icon type={IconType.CHAT} width={14} height={14} />
										</S.AssetTableActionButton>
										<S.AssetTableActionButton
											onClick={() => onDeleteAsset(asset)}
											title="Delete asset"
											aria-label="Delete asset"
										>
											<Icon type={IconType.TRASH} width={14} height={14} />
										</S.AssetTableActionButton>
										<S.AssetTableActionButton
											onClick={() => onDownloadAsset(asset)}
											title="Download asset"
											aria-label="Download asset"
										>
											<Icon type={IconType.LINE_ARROW_DOWN} width={14} height={14} />
										</S.AssetTableActionButton>
									</S.AssetTableActions>
								</S.AssetTableRow>
							);
						})}
					</S.AssetTable>
				) : (
					<>
						{(Object.keys(filteredByCategory) as AssetCategory[]).map((category) => {
							const categoryAssets = filteredByCategory[category];
							if (categoryAssets.length === 0) return null;

							return (
								<S.CategorySection key={category}>
									<S.CategoryHeader>
										<S.CategoryDot $color={categoryColors[category]} />
										<S.CategoryLabel>{categoryLabels[category]}</S.CategoryLabel>
										<S.CategoryCount>({categoryAssets.length})</S.CategoryCount>
									</S.CategoryHeader>
									<S.AssetGrid>
										{categoryAssets.map((asset) => {
											return (
												<S.AssetCard key={asset.path} $isNew={recentlyAdded.has(asset.path)}>
													<S.ChatButton
														onClick={() =>
															dispatch(toggleDsPick({ kind: 'asset', assetPath: asset.path }))
														}
														title="Add to chat"
													>
														<Icon type={IconType.CHAT} width={14} height={14} />
													</S.ChatButton>
													<S.MoveButtonWrapper>
														<DropdownMenu menuItems={getMoveMenuItems(asset)}>
															<S.MoveButton title="Move to...">
																<Icon type={IconType.FOLDER} width={14} height={14} />
															</S.MoveButton>
														</DropdownMenu>
													</S.MoveButtonWrapper>
													<S.DownloadButton
														onClick={() => onDownloadAsset(asset)}
														title="Download asset"
														aria-label="Download asset"
													>
														<Icon type={IconType.LINE_ARROW_DOWN} width={14} height={14} />
													</S.DownloadButton>
													<S.TrashButton
														onClick={() => onDeleteAsset(asset)}
														title="Delete asset"
														aria-label="Delete asset"
													>
														<Icon type={IconType.TRASH} width={14} height={14} />
													</S.TrashButton>
													<S.AssetImageContainer>
														{asset.mimeType.startsWith('image/') ? (
															<S.AssetImage src={getAssetUrl(asset.casHash)} alt={asset.name} />
														) : (
															<S.AssetMimeType>{asset.mimeType}</S.AssetMimeType>
														)}
													</S.AssetImageContainer>
													<S.AssetName>{asset.name}</S.AssetName>
													<S.AssetPath>{asset.path}</S.AssetPath>
												</S.AssetCard>
											);
										})}
									</S.AssetGrid>
								</S.CategorySection>
							);
						})}
					</>
				)}
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default AssetsView;
